import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

interface MaintenanceMode {
  enabled: boolean
  message?: string
  allow_preview?: boolean
}

export interface HeroSettings {
  background_image?: string
  background_opacity?: number
  logo_image?: string
  logo_link?: string
  title?: string
  title_highlight?: string
  description?: string
  button1_text?: string
  button2_text?: string
}

export interface PageVisibility {
  products: boolean
  gallery: boolean
  certificates: boolean
  about: boolean
  contact: boolean
}

export interface ProductPageFeature {
  icon: string
  title: string
  description: string
}

interface SiteSettings {
  maintenance_mode: MaintenanceMode
  hero_settings?: HeroSettings
  page_visibility?: PageVisibility
  product_page_features?: ProductPageFeature[]
  contact_info?: {
    company_name: string
    address: string
    city: string
    country: string
    phone: string
    email: string
    secondary_address?: string
    secondary_city?: string
    secondary_country?: string
    secondary_phone?: string
    secondary_email?: string
  }
  social_links?: {
    linkedin?: string
    facebook?: string
    twitter?: string
    instagram?: string
    youtube?: string
  }
}

// Check if preview mode is enabled via URL parameter
export function isPreviewMode(): boolean {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  return params.get('preview') === 'true'
}

// Hook to get maintenance mode status
export function useMaintenanceMode() {
  return useQuery({
    queryKey: ['engitronix-maintenance-mode'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('engitronix_site_settings')
        .select('value')
        .eq('key', 'maintenance_mode')
        .single()

      if (error) {
        // Default to false if setting doesn't exist
        console.warn('Maintenance mode setting not found:', error)
        return { enabled: false, message: '', allow_preview: true } as MaintenanceMode
      }

      return data.value as MaintenanceMode
    },
    staleTime: 30000, // Cache for 30 seconds
    refetchOnWindowFocus: false,
  })
}

// Hook to get all site settings
export function useSiteSettings() {
  return useQuery({
    queryKey: ['engitronix-site-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('engitronix_site_settings')
        .select('key, value')

      if (error) {
        console.error('Failed to load site settings:', error)
        return {} as SiteSettings
      }

      // Convert array to object
      const settings: Partial<SiteSettings> = {}
      data.forEach((item) => {
        ;(settings as Record<string, unknown>)[item.key] = item.value
      })

      return settings as SiteSettings
    },
    staleTime: 60000, // Cache for 1 minute
  })
}

// Hook to update maintenance mode
export function useUpdateMaintenanceMode() {
  const updateMaintenanceMode = async (settings: MaintenanceMode) => {
    const { error } = await supabase
      .from('engitronix_site_settings')
      .update({ value: settings })
      .eq('key', 'maintenance_mode')

    if (error) throw error
    return true
  }

  return { updateMaintenanceMode }
}

// Hook to get hero settings
export function useHeroSettings() {
  return useQuery({
    queryKey: ['engitronix-hero-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('engitronix_site_settings')
        .select('value')
        .eq('key', 'hero_settings')
        .single()

      if (error) {
        // Default settings if not found
        return { background_image: '', background_opacity: 30 } as HeroSettings
      }

      return data.value as HeroSettings
    },
    staleTime: 60000,
  })
}

// Default page visibility (all pages visible)
const defaultPageVisibility: PageVisibility = {
  products: true,
  gallery: true,
  certificates: true,
  about: true,
  contact: true,
}

// Hook to get page visibility settings
export function usePageVisibility() {
  return useQuery({
    queryKey: ['engitronix-page-visibility'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('engitronix_site_settings')
        .select('value')
        .eq('key', 'page_visibility')
        .single()

      if (error) {
        // Default: all pages visible
        return defaultPageVisibility
      }

      return { ...defaultPageVisibility, ...data.value } as PageVisibility
    },
    staleTime: 60000,
  })
}

// Default product page features
const defaultProductPageFeatures: ProductPageFeature[] = [
  { icon: 'ShieldCheck', title: 'CE Certified', description: 'All products meet European safety and quality standards.' },
  { icon: 'Globe', title: 'Global Reach', description: 'Serving customers in over 30 countries worldwide.' },
  { icon: 'Cpu', title: 'Advanced Technology', description: 'Latest innovations in fuel and industrial automation.' },
  { icon: 'Package', title: 'Custom Solutions', description: 'Tailored configurations to meet your specific needs.' },
]

// Hook to get product page features
export function useProductPageFeatures() {
  return useQuery({
    queryKey: ['engitronix-product-page-features'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('engitronix_site_settings')
        .select('value')
        .eq('key', 'product_page_features')
        .single()

      if (error) {
        return defaultProductPageFeatures
      }

      return (data.value as ProductPageFeature[]) || defaultProductPageFeatures
    },
    staleTime: 60000,
  })
}
